import type { ReactElement } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'

import { SlugChangeModal } from '@components/modals/SlugChangeModal'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import {
  AlertModal,
  ConfirmModal,
  Dropdown,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { reverse, routing } from '@utils/routing'

import { PRODUCT_SETTINGS_VIEW_TITLE } from '../../ProductSettings'
import { useOfferQuery } from '../../hooks'
import { useArchiveProduct } from '../../hooks/useArchiveProduct'
import { useDuplicateProduct } from '../../hooks/useDuplicateProduct'
import { useCustomizationV4Query } from '../hooks/useCustomizationV4Query'

export type ProductToolbarProps = {
  disabledArchive?: boolean
  onChangeUrl: (slug: string) => void
  onOrderSamples: (() => void) | undefined
  onUnarchive: () => void
}

export const ProductToolbar = ({
  disabledArchive = false,
  onChangeUrl,
  onOrderSamples,
  onUnarchive,
}: ProductToolbarProps) => {
  const adminMode = useAdminModeContext()
  const { open } = useModalContext()
  const media = useMediaQueryContext()
  const { hasPermission, hasRole } = useDashboardPermissions()
  const { productId = '' } = useParams<'productId'>()
  const { offerQuery } = useOfferQuery([productId])
  const { customizationV4Query } = useCustomizationV4Query([offerQuery.data?.customizationId!], {
    enabled: Boolean(offerQuery.data?.customizationId),
  })
  const { openDuplicateProductModal } = useDuplicateProduct()
  const { openArchiveProductModal } = useArchiveProduct()
  const { currentShop } = useCurrentShop()
  const productName = offerQuery.data?.name
  const slug = offerQuery.data?.slug
  const id = offerQuery.data?.id
  const customizationId = offerQuery.data?.customizationId
  const status = offerQuery.data?.state.status
  const hasDisabledDuplicateOption =
    !adminMode?.isActive &&
    (offerQuery.data?.manufacturingService === 'FOURTHWALL_BESPOKE' ||
      offerQuery.data?.fulfillingService === 'CART_JAZZ')

  const handleChangeUrlClick = () => {
    open(SlugChangeModal, {
      title: 'Edit product URL',
      slug: slug || '',
      shopBaseUri: currentShop?.baseUri || '',
      prepend: 'products',
      onSubmit: (newSlug) => {
        onChangeUrl(newSlug)
      },
    })
  }

  const handleArchiveClick = () => {
    openArchiveProductModal({
      name: productName ?? '',
      id: id ?? '',
      offerType: offerQuery.data?.offerType,
    })
  }

  const handleUnarchiveClick = () => {
    if (offerQuery.data?.customizationId) {
      open(ConfirmModal, {
        title: 'Are you sure you want to unarchive this product?',
        text: 'To unarchive this product you’ll need to review it in the product designer to ensure the design looks good.',
        confirmLabel: 'Unarchive and review',
        onConfirm: async () => {
          if (customizationV4Query.data?.designerVersion === 'PRINTFUL') {
            window.location.assign(
              reverse(routing.printfulEmbededDesigner.customizationEdit, {
                customizationId: String(customizationId),
                productId: String(id),
              }),
            )
          } else {
            window.location.assign(
              reverse(routing.productDesignerV3.customizationEdit, {
                customizationId: String(customizationId),
                productId: String(id),
              }),
            )
          }
        },
      })
    } else {
      onUnarchive()
    }
  }

  const handleDuplicateOffer = () => {
    if (!productName || !id) {
      return
    }

    if (hasDisabledDuplicateOption) {
      return open(AlertModal, {
        title: 'Duplicate option not available',
        text: (
          <div>
            Only creator fulfilled products can be duplicated, please{' '}
            <a
              href="mailto:support@fourthwall.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline' }}
            >
              reach out to support
            </a>{' '}
            for any questions.
          </div>
        ),
      })
    }

    openDuplicateProductModal({
      name: productName,
      id,
      onSuccess: (duplicatedOffer) => {
        window.location.href = reverse(routing.products.all.product.self, {
          productId: duplicatedOffer.id,
        })
      },
    })
  }

  const getDropdownItems = (): ReactElement[] => {
    const dropdownItems: ReactElement[] = []

    if (onOrderSamples && hasPermission('products.orderSamples')) {
      dropdownItems.push(<Dropdown.Item label="Order samples" onClick={onOrderSamples} />)
    }

    if (hasPermission('products.edit.url')) {
      dropdownItems.push(<Dropdown.Item label="Change URL" onClick={handleChangeUrlClick} />)
    }

    if (hasPermission('products.duplicate')) {
      dropdownItems.push(<Dropdown.Item label="Duplicate" onClick={handleDuplicateOffer} />)
    }

    if (hasPermission('products.advancedSettings')) {
      dropdownItems.push(
        <Dropdown.Item
          label={PRODUCT_SETTINGS_VIEW_TITLE}
          to={reverse(routing.products.all.product.settings, { productId })}
        />,
      )
    }

    if (hasRole('ROLE_COSTS_WRITE')) {
      dropdownItems.push(
        <Dropdown.Item
          label="Costs"
          to={reverse(routing.products.all.product.costs, { productId })}
        />,
      )
    }

    if (hasPermission('products.archive')) {
      if (status === 'ARCHIVED') {
        dropdownItems.push(
          <Dropdown.Item
            label="Unarchive"
            onClick={handleUnarchiveClick}
            disabled={disabledArchive}
          />,
        )
      } else {
        dropdownItems.push(
          <Dropdown.Item label="Archive" onClick={handleArchiveClick} disabled={disabledArchive} />,
        )
      }
    }

    return dropdownItems
  }

  const dropdownItems = getDropdownItems()

  if (!dropdownItems.length) {
    return null
  }

  return (
    <Dropdown content={dropdownItems.map((dropdownItem) => dropdownItem)} position="bottom-end">
      <ToolbarButton
        label="Settings"
        icon={DotsHorizontalIcon}
        size={media.tablet ? 'medium' : 'small'}
      />
    </Dropdown>
  )
}
